// src/resources/reports/ReportPushNotifications/types.ts
//
// Contrato do endpoint GET /dashboard-v2/push-details — espelha §3 do spec
// docs/superpowers/specs/2026-09-17-relatorio-push-notifications-design.md (pontuax-backend).
// O backend está sendo escrito em paralelo com este mesmo contrato, mesmo
// espírito de ReportAppPenetration/types.ts (feature 153, entregue hoje).
import { formatNumber } from '../../../utils';

/**
 * Enum tipado (regra do CLAUDE.md — nunca comparar string solta) espelhando
 * `DispatchMessageOrigin` do backend (pontuax-backend/src/models/dispatchMessageOrigin.ts).
 *
 * Não reaproveita o `DispatchMessageOrigin` que já existe em `../../../models`:
 * aquele enum está incompleto pras 7 origens que este relatório recebe (falta
 * `gift-marketing-campaign`, `scoring-marketing-campaign` e `message`) e é
 * usado por telas fora do escopo desta mudança (UnifiedSend, DispatchMessageList)
 * — mexer nele arriscaria comportamento alheio por uma tela nova.
 */
export enum DispatchMessageOrigin {
  UnifiedSend = 'unified-send',
  RecurringSend = 'recurring-send',
  ChainMarketingCampaign = 'chain-marketing-campaign',
  GiftMarketingCampaign = 'gift-marketing-campaign',
  DiscountMarketingCampaign = 'discount-marketing-campaign',
  ScoringMarketingCampaign = 'scoring-marketing-campaign',
  Message = 'message',
}

// Rótulos em português — tabela do §2 do spec. "Campanha do clube de
// vantagens" em vez de "Campanha do Gift": a palavra "Gift" NUNCA aparece pro
// usuário (feedback_no_gift_word_user_facing / invariant do GIFTS_COUPON_FLOW).
export const ORIGIN_LABELS: Record<DispatchMessageOrigin, string> = {
  [DispatchMessageOrigin.UnifiedSend]: 'Envio único',
  [DispatchMessageOrigin.RecurringSend]: 'Envio recorrente',
  [DispatchMessageOrigin.ChainMarketingCampaign]: 'Campanha da rede',
  [DispatchMessageOrigin.GiftMarketingCampaign]: 'Campanha do clube de vantagens',
  [DispatchMessageOrigin.DiscountMarketingCampaign]: 'Campanha de desconto',
  [DispatchMessageOrigin.ScoringMarketingCampaign]: 'Campanha de pontuação',
  [DispatchMessageOrigin.Message]: 'Mensagem avulsa',
};

/**
 * Legenda de uma linha embaixo de cada tipo de envio. "Campanha do clube" e
 * "Envio único" não dizem nada pra quem não montou a régua: a diferença entre
 * um disparo manual pra base inteira e uma campanha que dispara uma vez por
 * cliente é justamente o que explica "12 mensagens em 5.554 disparos".
 */
export const ORIGIN_CAPTIONS: Record<DispatchMessageOrigin, string> = {
  [DispatchMessageOrigin.UnifiedSend]: 'disparo manual, de uma vez, para a base escolhida',
  [DispatchMessageOrigin.RecurringSend]: 'régua que repete sozinha na frequência configurada',
  [DispatchMessageOrigin.ChainMarketingCampaign]: 'campanha da rede, agendada no painel',
  [DispatchMessageOrigin.GiftMarketingCampaign]: 'automática: dispara uma vez por cliente que ganha o brinde',
  [DispatchMessageOrigin.DiscountMarketingCampaign]: 'automática: avisa o cliente quando um desconto vale para ele',
  [DispatchMessageOrigin.ScoringMarketingCampaign]: 'automática: avisa sobre a regra de pontuação',
  [DispatchMessageOrigin.Message]: 'mensagem solta, fora de campanha',
};

// Ordem fixa pra filtro e legendas — a mesma ordem da tabela do §2.
export const ORIGIN_ORDER: DispatchMessageOrigin[] = [
  DispatchMessageOrigin.UnifiedSend,
  DispatchMessageOrigin.RecurringSend,
  DispatchMessageOrigin.ChainMarketingCampaign,
  DispatchMessageOrigin.GiftMarketingCampaign,
  DispatchMessageOrigin.DiscountMarketingCampaign,
  DispatchMessageOrigin.ScoringMarketingCampaign,
  DispatchMessageOrigin.Message,
];

export const ORIGIN_CHOICES = ORIGIN_ORDER.map(id => ({ id, name: ORIGIN_LABELS[id] }));

/** Funil bruto — usado tanto pro período atual quanto pro anterior (§3 query 4, só agregado). */
export interface FunnelTotals {
  messages: number;
  dispatches: number;
  sent: number;
  delivered: number;
  read: number;
  clicked: number;
  errors: number;
  deliveryRate: number | null; // delivered / sent
  readRate: number | null; // read / sent
  clickRate: number | null; // clicked / sent
  /**
   * Cliente recebeu a push, NÃO tocou nela, e abriu o app em até 24h (GSD
   * `influencedOpenTracker`, spec docs/superpowers/specs/2026-09-18-abertura-influenciada-push-design.md
   * §2, pontuax-backend). Campo novo no contrato — enquanto o backend não
   * sobe a coluna `total_influenced_opens`, chega `undefined`; por isso todo
   * consumo aqui usa `?? 0`, nunca lê `funnel.influencedOpens` direto.
   */
  influencedOpens?: number;
}

/** Delta em pontos percentuais das taxas do funil vs período anterior — nunca % relativo. */
export interface DeltaPp {
  deliveryRate: number | null;
  readRate: number | null;
  clickRate: number | null;
  influencedOpenRate: number | null;
  engagementRate: number | null;
}

/**
 * `influencedOpens` chega cru do backend (contagem), mas a TAXA e o
 * "engajamento total" (§3 do spec) são aritmética simples — calcular aqui em
 * vez de esperar o backend evita acoplar a tela a um segundo deploy só pra
 * expor uma divisão. `read` já é "tocou/abriu a caixa" e `influencedOpens` é
 * "abriu sem tocar"; os dois eventos são mutuamente exclusivos (o tracker do
 * backend só marca `influencedOpen` em stub SEM `read`/`clicked`), então
 * somar não conta ninguém duas vezes.
 */
export const influencedOpenRate = (f: FunnelTotals): number | null =>
  f.sent > 0 ? (f.influencedOpens ?? 0) / f.sent : null;

export const engagementRate = (f: FunnelTotals): number | null =>
  f.sent > 0 ? (f.read + (f.influencedOpens ?? 0)) / f.sent : null;

/** Mesmo arredondamento do `pushDeltaPp` do backend (dashboard-v2-helpers.ts) — pp, nunca % relativo. */
export const ratePp = (current: number | null, previous: number | null): number | null =>
  current === null || previous === null ? null : Math.round((current - previous) * 1000) / 10;

/**
 * Régua de mercado (§3 item 4 do spec) — Airship, "Mobile App Push
 * Notification Benchmarks 2025", taxa de ABERTURA calculada sobre ENVIADAS
 * (mesma base que a nossa `readRate`), por isso dá pra comparar direto sem
 * conversão. Constante única e comentada pra facilitar atualização quando
 * sair a edição seguinte do relatório — trocar só os 8 números abaixo.
 * Fonte: https://www.airship.com/resources/explainer/mobile-app-push-notification-benchmarks/ (2025).
 */
export const PUSH_OPEN_RATE_BENCHMARKS_2025 = {
  source: 'Airship — Mobile App Push Notification Benchmarks 2025',
  android: { p10: 0.007, median: 0.034, p90Retail: 0.081, p90: 0.107 },
  ios: { p10: 0.008, median: 0.031, p90Retail: 0.054, p90: 0.08 },
};

/**
 * Nosso funil não separa Android de iOS (o disparo vai pro público misto da
 * rede), então a régua usa a MÉDIA dos dois cortes do Airship como base de
 * comparação — é a mesma conta que dá "3,2%" pra mediana (3,4% + 3,1%) / 2,
 * o número citado no pedido original.
 */
export const blendedOpenRateBenchmark = () => {
  const { android, ios } = PUSH_OPEN_RATE_BENCHMARKS_2025;
  const avg = (a: number, b: number) => (a + b) / 2;
  return {
    p10: avg(android.p10, ios.p10),
    median: avg(android.median, ios.median),
    p90Retail: avg(android.p90Retail, ios.p90Retail),
    p90: avg(android.p90, ios.p90),
  };
};

export type BenchmarkTier = 'below-p10' | 'below-median' | 'below-retail-p90' | 'below-p90' | 'top';

/** Classifica a taxa de leitura na régua — ordem crescente: p10 < mediana < p90 do varejo < p90 geral. */
export const classifyOpenRate = (readRate: number | null): BenchmarkTier | null => {
  if (readRate === null) return null;
  const b = blendedOpenRateBenchmark();
  if (readRate < b.p10) return 'below-p10';
  if (readRate < b.median) return 'below-median';
  if (readRate < b.p90Retail) return 'below-retail-p90';
  if (readRate < b.p90) return 'below-p90';
  return 'top';
};

export const BENCHMARK_TIER_LABEL: Record<BenchmarkTier, (medianPct: string) => string> = {
  'below-p10': () => 'nos 10% piores do mercado',
  'below-median': (medianPct: string) => `abaixo da mediana do mercado (${medianPct})`,
  'below-retail-p90': () => 'acima da mediana, ainda abaixo do topo do varejo',
  'below-p90': () => 'no topo do varejo',
  'top': () => 'nos 10% melhores do mercado',
};

export interface OriginBreakdownRow {
  origin: DispatchMessageOrigin;
  messages: number;
  dispatches: number;
  sent: number;
  delivered: number;
  read: number;
  clicked: number;
  deliveryRate: number | null;
  readRate: number | null;
  clickRate: number | null;
}

export interface SeriesPoint {
  date: string; // YYYY-MM-DD, relógio de São Paulo (toSaoPauloNaive)
  messages: number;
  sent: number;
  delivered: number;
  read: number;
  clicked: number;
  readRate: number | null;
  /**
   * Títulos que saíram naquele dia — pro tooltip da linha do tempo (§4 bloco 3:
   * "tooltip lista os títulos que saíram naquele dia... é o quando foi cada
   * envio"). Extensão sobre o esqueleto de `series` do §3 (que só tem os
   * agregados numéricos): sem essa lista o tooltip não responde a pergunta que
   * o bloco existe pra responder.
   */
  titles: string[];
}

export interface MessageRow {
  messageId: string;
  origin: DispatchMessageOrigin;
  title: string;
  pushMessage: string;
  pushScreen: string | null;
  dispatches: number;
  sent: number;
  delivered: number;
  read: number;
  clicked: number;
  deliveryRate: number | null;
  readRate: number | null;
  clickRate: number | null;
  influencedOpens?: number; // ver comentário em FunnelTotals — opcional até o backend subir a coluna
  firstSentAt: string; // ISO
  lastSentAt: string; // ISO
}

export interface HourlyPoint {
  hour: number; // 0–23, relógio de São Paulo
  sent: number;
  read: number;
  readRate: number | null;
}

export interface PushDetailsData {
  id: 'push-details';
  funnel: FunnelTotals;
  previous: FunnelTotals; // período anterior de mesma duração — só o agregado (§3 query 4)
  deltaPp: DeltaPp;
  byOrigin: OriginBreakdownRow[]; // ordenado por sent desc
  series: SeriesPoint[];
  messages: MessageRow[]; // até as 100 maiores por `sent`
  messagesTruncated: boolean; // true = existem mais mensagens que as listadas acima
  messagesTotal: number; // total real de mensagens no período, truncado ou não
  hourly: HourlyPoint[]; // 0–23
  legacyFallback: boolean; // true quando algum disparo veio do customer_dispatch_message (período antigo, sem resumo)
}

// ─── Formatação pt-BR — sempre via formatNumber (nunca Intl solto) ────────────
export const fmtInt = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : formatNumber(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const fmtPct = (v: number | null | undefined, decimals = 1): string =>
  v === null || v === undefined ? '—' : `${formatNumber(v * 100, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;

// Taxa pequena (ex.: leitura de campanha automática pode ficar < 1%) não
// arredonda pra "0%" — mesma regra do EngagementSection (pctSmall) do Painel 360.
export const fmtPctSmall = (v: number | null | undefined): string => {
  if (v === null || v === undefined || !isFinite(v) || v <= 0) return v === null || v === undefined ? '—' : '0%';
  const p = v * 100;
  return p < 1 ? `${formatNumber(p, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : fmtPct(v);
};

/**
 * Variação da taxa contra o período anterior. O backend JÁ manda em pontos percentuais
 * (`Math.round((atual - anterior) * 1000) / 10`), então aqui NÃO se multiplica por 100 de novo —
 * era o que fazia 20,5 virar "2.050,0". Sai com o símbolo %, que é o que o leitor entende.
 */
export const fmtDelta = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '—';
  const abs = formatNumber(Math.abs(v), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${v >= 0 ? '▲' : '▼'} ${abs}%`;
};

/**
 * Teto do eixo de taxa (em %) ajustado aos dados: taxa de leitura de push vive abaixo de 2%, e
 * um eixo fixo de 0–100% achatava a linha no chão do gráfico. Pega o maior valor do período,
 * dá 25% de folga e arredonda pra um número redondo (1, 2, 5 × 10^k) pros ticks ficarem legíveis.
 * Sem dado nenhum, devolve 1% — eixo 0–1% em vez de 0–0%.
 */
export const niceRateDomainMax = (values: (number | null | undefined)[]): number => {
  const max = values.reduce<number>((acc, v) => (v === null || v === undefined || !isFinite(v) ? acc : Math.max(acc, v)), 0);
  if (max <= 0) return 1;
  const target = max * 1.25;
  const magnitude = Math.pow(10, Math.floor(Math.log10(target)));
  const normalized = target / magnitude;
  // Escada fina de propósito: só 1/2/5/10 faria uma taxa de 45% virar eixo de 100%.
  const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find(candidate => normalized <= candidate) ?? 10;
  // Taxa não passa de 100%: sem o teto, 98% de leitura viraria um eixo até 150%.
  return Math.min(100, Number((step * magnitude).toFixed(6)));
};

/** Rótulo do eixo/tooltip de taxa: casas decimais só quando a escala é pequena. */
export const fmtAxisPct = (value: number, axisMax: number): string =>
  `${value.toLocaleString('pt-BR', { maximumFractionDigits: axisMax < 5 ? 1 : 0 })}%`;
